/**
 * The Commissioner's Pick'em settings dialog (ISSUE-1306), opened by
 * `?dialog=pickem-settings` so browser back closes it.
 *
 * It edits the same fields as the create step, prefilled from the pool's
 * current settings. The server says which fields are frozen (scoring and how
 * picks are made, once the first round opens) and every other change starts
 * with the next round that has not opened yet. The rules text under the
 * fields is the server's preview of the draft. A dirty dialog asks before it
 * discards; a refusal goes to the shared error modal.
 */
import {
  usePickemSettingsUpdate,
  usePickemSetupPreview,
} from "@sportsmet/contest-sdk/private";
import {
  Button,
  confirmDestructive,
  Dialog,
  OVERLAY_FOOTER_LAYOUT,
} from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  CREATE_POOL_MISSING_ELEMENT_ID,
  CreatePoolMissingSummary,
} from "../createPool/CreatePoolMissingSummary";
import {
  PICKEM_SETTINGS_CANCEL_LABEL,
  PICKEM_SETTINGS_DIALOG_TITLE,
  PICKEM_SETTINGS_DISCARD_BODY,
  PICKEM_SETTINGS_DISCARD_CANCEL,
  PICKEM_SETTINGS_DISCARD_CONFIRM,
  PICKEM_SETTINGS_DISCARD_TITLE,
  PICKEM_SETTINGS_SAVE_LABEL,
  PICKEM_SETTINGS_SAVING_LABEL,
  pickemPendingChangeLine,
} from "./pickemCopy";
import {
  buildPickemSetupPayload,
  isSamePickemSetup,
  pickemDraftFromSetup,
  pickemSetupMissingItems,
  type typePickemSetupDraft,
} from "./pickemCreateSetupDraft";
import { PickemRulesPreview } from "./PickemRulesLines";
import { PickemSettingsFields } from "./PickemSettingsFields";
import type { typePickemPoolSettings } from "./pickemTypes";

export type typePickemSettingsDialogProps = {
  open: boolean;
  poolId: string;
  leagueKey: string;
  settings: typePickemPoolSettings;
  onClose: () => void;
  onSaved: (effectiveFromRoundLabel: string | null) => void;
};

export const PickemSettingsDialog = (
  props: typePickemSettingsDialogProps,
): ReactNode => {
  const { settings } = props;
  const [draft, setDraft] = useState<typePickemSetupDraft>(() =>
    pickemDraftFromSetup({ setup: settings, leagueKey: props.leagueKey }),
  );
  const base = pickemDraftFromSetup({
    setup: settings,
    leagueKey: props.leagueKey,
  });

  // Every open starts from what the pool has now.
  useEffect(() => {
    if (props.open) {
      setDraft(
        pickemDraftFromSetup({ setup: settings, leagueKey: props.leagueKey }),
      );
    }
    // Reseed on open only, never while the Commissioner is typing, so the
    // settings and league are deliberately left out of the dependency list.
    // This repo's lint config does not enable the react-hooks rules.
  }, [props.open]);

  const payload = buildPickemSetupPayload(draft);
  const preview = usePickemSetupPreview(
    { leagueKey: props.leagueKey, setup: payload },
    {
      enabled: props.open && props.leagueKey !== "" && payload !== null,
      placeholderData: (previous) => previous,
    },
  );
  const previewData = preview.data?.data ?? null;
  const update = usePickemSettingsUpdate();

  const isDirty = !isSamePickemSetup(draft, base);
  const missingItems = pickemSetupMissingItems(draft);

  async function requestClose(): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: PICKEM_SETTINGS_DISCARD_TITLE,
      body: PICKEM_SETTINGS_DISCARD_BODY,
      confirmLabel: PICKEM_SETTINGS_DISCARD_CONFIRM,
      cancelLabel: PICKEM_SETTINGS_DISCARD_CANCEL,
    });
    if (isConfirmed) props.onClose();
  }

  async function save(): Promise<void> {
    if (payload === null) return;
    let result: Awaited<ReturnType<typeof update.mutateAsync>>;
    try {
      result = await update.mutateAsync({
        poolId: props.poolId,
        settings: payload,
      });
    } catch {
      return;
    }
    if (showApiError(result)) return;
    props.onSaved(result.data?.effectiveFromRoundLabel ?? null);
  }

  const pendingLabel = settings.pendingChangeFromRoundLabel ?? null;

  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
      isDirty={isDirty}
      onRequestClose={() => {
        void requestClose();
      }}
      title={PICKEM_SETTINGS_DIALOG_TITLE}
      footer={
        <div className="flex w-full flex-col gap-2">
          <CreatePoolMissingSummary items={missingItems} />
          <div className={OVERLAY_FOOTER_LAYOUT}>
            <Button
              variant="secondary"
              data-testid="pickem-settings-cancel"
              onClick={() => {
                if (isDirty) {
                  void requestClose();
                } else {
                  props.onClose();
                }
              }}
            >
              {PICKEM_SETTINGS_CANCEL_LABEL}
            </Button>
            <Button
              data-testid="pickem-settings-save"
              disabled={!isDirty || payload === null}
              loading={update.isPending}
              loadingLabel={PICKEM_SETTINGS_SAVING_LABEL}
              aria-describedby={
                missingItems.length > 0
                  ? CREATE_POOL_MISSING_ELEMENT_ID
                  : undefined
              }
              onClick={() => {
                void save();
              }}
            >
              {PICKEM_SETTINGS_SAVE_LABEL}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-6" data-testid="pickem-settings-dialog">
        {pendingLabel !== null ? (
          <p className="text-caption text-ink-secondary">
            {pickemPendingChangeLine(pendingLabel)}
          </p>
        ) : null}
        <PickemSettingsFields
          draft={draft}
          onChange={setDraft}
          hasSpreads={previewData?.hasSpreads ?? true}
          frozenFields={settings.frozenFields}
          fieldErrors={payload === null ? [] : (previewData?.fieldErrors ?? [])}
        />
        <PickemRulesPreview
          isDraftComplete={payload !== null}
          rulesLines={previewData?.rulesLines ?? null}
          testId="pickem-settings-preview-rules"
        />
      </div>
    </Dialog>
  );
};
